import React, {
    FC, useCallback, useEffect, useMemo, useState,
} from 'react';

import { Button, message, Tooltip } from 'antd';
import {
    LinkOutlined, LockOutlined, StopOutlined,
} from '@ant-design/icons';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { AutoComplete } from '@/components/Common/UIKit';
import PolicySelect from '@/components/Common/PolicySelect';
import PolicyDescription from '@/components/Common/PolicyDescription';
import Spinner from '@/components/Common/Spin';
import GroupDetailsModal from '@/components/Common/SharingBlock/GroupDetailsModal';
import RecipientRow from './RecipientRow';
import MissingDetailsRevokableItem from './MissingDetailsRevokableItem';
import { tryCopyToClipboard, PolicyAggregate, getPolicyAggregate } from '@/components/utils';
import validateEmail from '@/components/validators/validateEmail';
import { ForwardingStatus } from '@/components/FilesTable/Modals/RevocationWarningModal/helpers';
import { useStores } from '@/components/hooks';
import { SharedUserToDisplay } from '@/stores';
import { Policy, RecipientRoles } from '@/types/types';
import styles from './PermissionDrawer.module.scss';

const nameSpace = 'filesTable.permissionDrawer';

interface PermissionsViewProps {
    isLoading: boolean;
    isPolicyUpdating: boolean;
    isUpdating: boolean;
    isLocked: boolean;
    isFolder: boolean;
    ruleAssignedChildren: { fid: string; filename: string; policyId: string; ruleName?: string }[];
    selectedPolicy: Policy;
    policyAggregate: PolicyAggregate;
    showPhoneColumn: boolean;
    showPasswordColumn: boolean;
    users: SharedUserToDisplay[];
    hasPolicies: boolean;
    isPolicyChangeBlocked: boolean;
    usersPhonesMap: Map<string, { phonesVector: { currentPhone: { phone?: string; prefix?: string } | null } }>;
    shareLinks: Map<string, { link?: string }>;
    singleRecipientIdentity: string;
    forwardingStatus: ForwardingStatus;
    onPolicyChange: (policyId: string) => void;
    onRoleChange: (email: string, role: RecipientRoles, user: SharedUserToDisplay) => void;
    onRevoke: (email: string, user: SharedUserToDisplay) => void;
    onRevokeAllDomain: (email: string) => void;
    checkIsEditable: (userId: string) => boolean;
    canAddRecipients: boolean;
    addFlowSuspended: boolean;
    onResumeAddFlow: () => void;
    onClose: () => void;
    onAddRecipients: (initialEmail?: string, initialGroup?: { id: string; name: string }) => void;
    assignPolicyRuleName?: string;
}

const PermissionsView: FC<PermissionsViewProps> = observer(({
    isLoading,
    isPolicyUpdating,
    isUpdating,
    isLocked,
    isFolder,
    ruleAssignedChildren,
    selectedPolicy,
    policyAggregate,
    showPhoneColumn,
    showPasswordColumn,
    users,
    hasPolicies,
    isPolicyChangeBlocked,
    usersPhonesMap,
    shareLinks,
    singleRecipientIdentity,
    forwardingStatus,
    onPolicyChange,
    onRoleChange,
    onRevoke,
    onRevokeAllDomain,
    checkIsEditable,
    canAddRecipients,
    addFlowSuspended,
    onResumeAddFlow,
    onClose,
    onAddRecipients,
    assignPolicyRuleName,
}) => {
    const { t } = useTranslation();
    const { sharedUsersStore, usersListStore, policyStore, usersPhonesStore } = useStores();

    const isPolicyRestricted = !!selectedPolicy && !selectedPolicy.settings?.access?.length;

    const [emailInput, setEmailInput] = useState('');
    const isValidEmail = emailInput.trim().length > 0 && validateEmail(emailInput.trim());

    useEffect(() => {
        sharedUsersStore.fetchEmailsList();
    }, []);

    const emailOptions = useMemo(() => {
        if (!emailInput) return [];
        const lower = emailInput.toLowerCase();
        return [
            ...sharedUsersStore.emailsList
                .map((email) => ({ label: email, value: email })),
            ...usersListStore.userGroupsList
                .map((g) => ({ label: g.groupName, value: g.groupId })),
        ].filter(({ label }) => label?.toLowerCase().startsWith(lower));
    }, [emailInput, sharedUsersStore.emailsList, usersListStore.userGroupsList]);

    const checkAlreadyShared = useCallback((value: string): boolean => {
        const lower = value.toLowerCase();
        const isAlreadyShared = users.some((u) => u.email.toLowerCase() === lower);
        if (isAlreadyShared) {
            message.info(t('sharingBlock.validation.alreadyShared', { email: value }));
            return true;
        }
        return false;
    }, [users, t]);

    const handleAddEmail = useCallback((): void => {
        const email = emailInput.trim();
        if (!validateEmail(email)) return;
        if (checkAlreadyShared(email)) return;
        setEmailInput('');
        onAddRecipients(email);
    }, [emailInput, onAddRecipients, checkAlreadyShared]);

    const handleSelect = useCallback((value: string): void => {
        if (checkAlreadyShared(value)) return;
        setEmailInput('');
        const group = usersListStore.userGroupsList.find((g) => g.groupId === value);
        if (group) {
            onAddRecipients(undefined, { id: group.groupId, name: group.groupName });
        } else {
            onAddRecipients(value);
        }
    }, [onAddRecipients, checkAlreadyShared, usersListStore.userGroupsList]);

    const assignedPolicyGroups = useMemo(() => {
        if (!isFolder || ruleAssignedChildren.length === 0) return [];
        const groupMap = new Map<string, typeof ruleAssignedChildren>();
        ruleAssignedChildren.forEach((child) => {
            if (!groupMap.has(child.policyId)) {
                groupMap.set(child.policyId, []);
            }
            groupMap.get(child.policyId).push(child);
        });
        return [...groupMap.entries()].map(([policyId, groupFiles]) => {
            const policy = policyStore.policyList.find((p) => p.id === policyId);
            return {
                policyId,
                policy,
                aggregate: policy ? getPolicyAggregate(policy) : null,
                files: groupFiles,
            };
        });
    }, [isFolder, ruleAssignedChildren, policyStore.policyList]);

    const blockedUsers = users.filter((u) => u.deleted);

    const [completedEmails, setCompletedEmails] = useState<Set<string>>(new Set());

    const needsDetails = showPhoneColumn || showPasswordColumn;

    const { completeUsers, missingUsers } = (() => {
        if (!needsDetails) return { completeUsers: users, missingUsers: [] as SharedUserToDisplay[] };

        const complete: SharedUserToDisplay[] = [];
        const missing: SharedUserToDisplay[] = [];

        users.forEach((user) => {
            if (user.recipientType === 'group' || completedEmails.has(user.email)) {
                complete.push(user);
                return;
            }

            const phoneInfo = usersPhonesMap.get(user.email)?.phonesVector?.currentPhone;
            const hasPhone = !!(phoneInfo?.phone && phoneInfo?.prefix);
            const phoneOk = !showPhoneColumn || hasPhone;

            const passwordInfo = sharedUsersStore.getRecipientPasswordInfo(user.email);
            const hasPassword = !!passwordInfo?.hasPassword;
            const passwordOk = !showPasswordColumn || hasPassword;

            if (phoneOk && passwordOk) {
                complete.push(user);
            } else {
                missing.push(user);
            }
        });

        return { completeUsers: complete, missingUsers: missing };
    })();

    const handleDetailsComplete = useCallback((email: string): void => {
        setCompletedEmails((prev) => new Set(prev).add(email));
    }, []);

    return (
        <>
            <Spinner spinning={isLoading} wrapperClassName={styles['content-spinner']}>
                <div className={styles.content} data-testid="permissionDrawer_permissionsBody">
                    {canAddRecipients && (
                        <div className={styles['add-email-row']} data-testid="permissionDrawer_addRecipientRow">
                            <AutoComplete
                                className={styles['add-email-input']}
                                options={emailOptions}
                                value={emailInput}
                                onChange={setEmailInput}
                                onSelect={handleSelect}
                                onKeyUp={(e) => {
                                    if (e.key === 'Enter' && isValidEmail) handleAddEmail();
                                }}
                                placeholder={t(`${nameSpace}.addRecipientEmail`)}
                                data-testid="permissionDrawer_addRecipientEmailInput"
                            />
                            {isValidEmail && (
                                <Button
                                    type="primary"
                                    className={styles['add-email-button']}
                                    onClick={handleAddEmail}
                                    data-testid="permissionDrawer_addRecipientSubmit"
                                >
                                    {t('general.buttons.add')}
                                </Button>
                            )}
                        </div>
                    )}

                    <div className={styles['section-header']} data-testid="permissionDrawer_policySectionTitle">
                        <span className={styles['section-title']}>
                            {isFolder
                                ? t(`${nameSpace}.parentPolicy`)
                                : isLocked
                                    ? t(`${nameSpace}.assignedPolicy`)
                                    : t(`${nameSpace}.choosePolicy`)}
                        </span>
                    </div>
                    <div
                        className={`${styles['section-content']} ${styles['policy-section']}`}
                        data-testid="permissionDrawer_policySection"
                    >
                        <div className={styles['policy-layout']}>
                            <div className={classNames(styles['policy-left'], { [styles['policy-left-restricted']]: isPolicyRestricted })}>
                                {selectedPolicy && (hasPolicies || isPolicyRestricted) && (
                                    <PolicySelect
                                        className={styles['policy-select']}
                                        selectedPolicyId={selectedPolicy.id}
                                        onChange={onPolicyChange}
                                        disabled={isLocked || isPolicyUpdating || isPolicyChangeBlocked}
                                        fallbackOption={isPolicyRestricted ? { value: selectedPolicy.id, label: selectedPolicy.title } : undefined}
                                    />
                                )}
                            </div>
                            {!isPolicyRestricted && (
                                <div className={styles['policy-right']}>
                                    {selectedPolicy && (
                                        <PolicyDescription policyAggregate={policyAggregate} />
                                    )}
                                </div>
                            )}
                        </div>

                        {isLocked && (
                            <div className={styles['locked-banner']} data-testid="permissionDrawer_lockedPolicyBanner">
                                <LockOutlined />
                                <span>
                                    <Trans
                                        t={t}
                                        i18nKey={`${nameSpace}.assignedDueToRule`}
                                        values={{
                                            ruleName: assignPolicyRuleName?.trim()
                                                || t(`${nameSpace}.policyAssignmentRuleFallback`),
                                        }}
                                    >
                                        {'Assigned due to '}
                                        <strong>{'{{ruleName}}'}</strong>
                                    </Trans>
                                </span>
                            </div>
                        )}
                    </div>

                    {isFolder && assignedPolicyGroups.length > 0 && (
                        <div data-testid="permissionDrawer_folderChildPolicies">
                            <div className={styles['section-header']} data-testid="permissionDrawer_folderChildPoliciesTitle">
                                <LockOutlined className={styles['section-icon']} />
                                <span className={styles['section-title']}>
                                    {t('uploadFiles.policyStep.assignedPolicy')}
                                </span>
                            </div>
                            {assignedPolicyGroups.map((group) => (
                                <div
                                    key={group.policyId}
                                    className={`${styles['section-content']} ${styles['policy-section']}`}
                                    data-testid={`permissionDrawer_folderChildPolicy_${group.policyId}`}
                                >
                                    <div className={styles['policy-layout']}>
                                        <div className={styles['policy-left']}>
                                            <PolicySelect
                                                className={styles['policy-select']}
                                                selectedPolicyId={group.policy?.id}
                                                disabled
                                            />
                                        </div>
                                        <div className={styles['policy-right']}>
                                            {group.aggregate && (
                                                <PolicyDescription
                                                    policyAggregate={group.aggregate}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles['assigned-items-label']}>
                                        {t(`${nameSpace}.assignedTo`)}
                                        {' '}
                                        <Tooltip
                                            placement="bottom"
                                            color="#091F56"
                                            title={(
                                                <div>
                                                    {group.files.map((f) => (
                                                        <div key={f.fid}>
                                                            {f.filename}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        >
                                            <span className={styles['assigned-items-link']}>
                                                {t(
                                                    `${nameSpace}.itemCount`,
                                                    {
                                                        count: group.files.length,
                                                        defaultValue: '{{count}} item',
                                                        defaultValue_plural: '{{count}} items',
                                                    },
                                                )}
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <Spinner spinning={isPolicyUpdating}>
                        <div
                            className={styles['recipients-section']}
                            data-testid="shareDialog_WhoHasAccess"
                        >
                            <div className={styles['table-header']} data-testid="permissionDrawer_recipientsTableHeader">
                                <div className={classNames(styles['col-email'], styles['col-header'])}>
                                    {t(`${nameSpace}.usersWithAccess`)}
                                </div>
                                {showPhoneColumn && (
                                    <div className={classNames(styles['col-phone'], styles['col-header'])}>
                                        {t(`${nameSpace}.phoneNumber`)}
                                    </div>
                                )}
                                {showPasswordColumn && (
                                    <div className={classNames(styles['col-password'], styles['col-header'])}>
                                        {t(`${nameSpace}.password`)}
                                    </div>
                                )}
                                <div className={classNames(styles['col-role'], styles['col-header'])}>
                                    {t(`${nameSpace}.role`)}
                                </div>
                            </div>

                            <div className={styles['recipients-list']} data-testid="permissionDrawer_recipientsList">
                                {completeUsers.length === 0 ? (
                                    <div className={styles['empty-recipients']} data-testid="permissionDrawer_emptyRecipients">
                                        {t(`${nameSpace}.noExistingRecipients`)}
                                    </div>
                                ) : completeUsers.map((user, rowIndex) => (
                                    <RecipientRow
                                        key={user.userId}
                                        user={user}
                                        allUsers={users}
                                        showPhoneColumn={showPhoneColumn}
                                        showPasswordColumn={showPasswordColumn}
                                        isEditable={checkIsEditable(user.userId)}
                                        isUpdating={isUpdating}
                                        usersPhonesMap={usersPhonesMap}
                                        forwardingStatus={forwardingStatus}
                                        onRoleChange={onRoleChange}
                                        onRevoke={onRevoke}
                                        onRevokeAllDomain={onRevokeAllDomain}
                                        isFirstRecipientRow={rowIndex === 0}
                                    />
                                ))}
                            </div>
                        </div>
                    </Spinner>

                    {missingUsers.length > 0 && (
                        <div className={styles['missing-details-section']} data-testid="permissionDrawer_missingDetailsSection">
                            <div className={styles['section-header']}>
                                <span className={styles['section-title']}>
                                    {t('uploadFiles.policyStep.missingDetails')}
                                </span>
                            </div>
                            <div className={styles['missing-details-list']} data-testid="permissionDrawer_missingDetailsList">
                                {missingUsers.map((user) => (
                                    <MissingDetailsRevokableItem
                                        key={user.userId}
                                        user={user}
                                        allUsers={users}
                                        showPhoneColumn={showPhoneColumn}
                                        showPasswordColumn={showPasswordColumn}
                                        forwardingStatus={forwardingStatus}
                                        onRevoke={onRevoke}
                                        onRevokeAllDomain={onRevokeAllDomain}
                                        onDetailsComplete={handleDetailsComplete}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    <div className={styles['footer-hint']} data-testid="permissionDrawer_policyChangeHint">
                        {t(`${nameSpace}.policyChangeHint`)}
                    </div>
                </div>
            </Spinner>

            <div className={styles['footer-area']} data-testid="permissionDrawer_footerArea">
                {blockedUsers.length > 0 && (
                    <Tooltip
                        placement="top"
                        color="#091F56"
                        title={(
                            <div>
                                {blockedUsers.map((u) => (
                                    <div key={u.email} className={styles['blocked-tooltip-email']}>
                                        {u.email}
                                    </div>
                                ))}
                            </div>
                        )}
                    >
                        <div className={styles['blocked-bar']} data-testid="permissionDrawer_blockedUsersBar">
                            <StopOutlined className={styles['blocked-bar-icon']} />
                            <span>
                                {t(`${nameSpace}.blockedUserCount`, {
                                    count: blockedUsers.length,
                                    defaultValue: '{{count}} Blocked user',
                                    defaultValue_plural: '{{count}} Blocked users',
                                })}
                            </span>
                        </div>
                    </Tooltip>
                )}

                <div className={styles.footer} data-testid="permissionDrawer_mainFooter">
                    <Button
                        type="link"
                        className={styles['close-button']}
                        onClick={addFlowSuspended ? onResumeAddFlow : onClose}
                        data-testid="permissionDrawer_close"
                    >
                        {t(addFlowSuspended
                            ? 'general.buttons.back'
                            : 'general.buttons.close')}
                    </Button>
                    <div className={styles['footer-right']} data-testid="permissionDrawer_footerActions">
                        <Button
                            className={styles['copy-link-button']}
                            icon={<LinkOutlined />}
                            data-testid="whoHasAccess_copyLink"
                            onClick={() => {
                                const linkData = shareLinks.get(singleRecipientIdentity);
                                if (linkData?.link) {
                                    tryCopyToClipboard(linkData.link, t('copyLink.link'));
                                }
                            }}
                        >
                            {t('copyLink.copyLink')}
                        </Button>
                    </div>
                </div>
            </div>

            {usersPhonesStore.phoneNumbersGroupId && (
                <GroupDetailsModal
                    showPhones={showPhoneColumn}
                    showPasswords={showPasswordColumn}
                />
            )}
        </>
    );
});

export default PermissionsView;
